import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, CardBody, Table } from 'reactstrap';

import TenderStatusBadge from './TenderStatusBadge';
import {
  formatCount,
  formatDisplayDate,
  formatFileSize,
  formatText,
} from './formatters';
import { fetchTenderAnnouncementById } from './services/tenderAnnouncementService';
import { usePublicLanguage } from './i18n/LanguageContext';
import { api } from '../../config';
import './PublicTenderResponsive.css';

// `api.API_URL` sudah berakhiran `/api`, dan `downloadUrl` dari backend juga
// berawalan `/api/...` (§6.4 dokumen desain) — digabung apa adanya akan
// menghasilkan `/api/api/...`. Buang akhiran `/api` dari API_URL dulu supaya
// hanya origin/host-nya yang dipakai.
const API_ORIGIN = api.API_URL.replace(/\/api\/?$/, '');

const PublicTenderAnnouncementDetail = () => {
  const { id } = useParams();
  const { t, language } = usePublicLanguage();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTenderAnnouncementById(id, language);
      setData(result);
    } catch (err) {
      setError(t('state.error'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, language, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pita hero ringkas: halaman ini duduk di atas gradasi biru setinggi satu
  // layar, jadi tautan kembali dan judul harus berteks terang agar terbaca.
  const renderHero = titleContent => (
    <div className='public-tender__hero public-tender__hero--compact'>
      <div className='public-tender__hero-overlay' aria-hidden='true' />
      <div className='public-tender__hero-accent' aria-hidden='true' />
      <div className='public-tender__hero-content'>
        <Link to='/tender-announcement' className='public-tender__back-link'>
          <i className='ri-arrow-left-line me-2' aria-hidden='true'></i>
          {t('detail.back')}
        </Link>
        {titleContent}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card className='public-tender__card border-0 overflow-hidden'>
        {renderHero(null)}
        <div className='public-tender__body'>
          <Card className='public-tender__detail-card'>
            <CardBody>
              <span className='public-tender__skeleton public-tender__skeleton--title' />
              <span className='public-tender__skeleton mt-3' />
              <span className='public-tender__skeleton mt-2' />
              <span className='public-tender__skeleton mt-2' />
            </CardBody>
          </Card>
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className='public-tender__card border-0 overflow-hidden'>
        {renderHero(null)}
        <div className='public-tender__body'>
          <Card className='public-tender__detail-card'>
            <CardBody>
              <div className='public-tender__empty text-center'>
                <i
                  className={`${error ? 'ri-error-warning-line' : 'ri-file-search-line'} public-tender__empty-icon`}
                  aria-hidden='true'
                ></i>
                <h5 className='mb-2'>
                  {error ? t('detail.errorTitle') : t('detail.notFoundTitle')}
                </h5>
                <p className='text-muted mb-3'>
                  {error ? t('detail.errorDesc') : t('detail.notFoundDesc')}
                </p>
                <Link to='/tender-announcement' className='btn btn-primary'>
                  {t('detail.back')}
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </Card>
    );
  }

  // API selalu mengirim actionLinks, tapi respons lama (mis. dari cache browser)
  // bisa saja belum memuatnya.
  const actionLinks = data.actionLinks ?? [];

  const infoRows = [
    [t('field.procurementNumber'), formatText(data.procurementNumber)],
    [t('field.titleProcurement'), formatText(data.titleProcurement)],
    [
      t('field.announcementDate'),
      formatDisplayDate(data.announcementDate, language),
    ],
    [t('field.procurementType'), formatText(data.procurementType)],
    [
      t('field.procurementTypeHeader'),
      formatText(data.procurementTypeHeaderLabel),
    ],
    [t('field.contractType'), formatText(data.contractType)],
    [
      t('field.status'),
      <TenderStatusBadge key='status' status={data.publicStatus} />,
    ],
    [
      t('field.deadline'),
      formatDisplayDate(data.proposalSubmissionDeadline, language),
    ],
    [t('field.awardedTo'), formatText(data.awardedToCompanyName)],
  ];

  return (
    <Card className='public-tender__card border-0 overflow-hidden'>
      {renderHero(
        <div className='public-tender__detail-header'>
          <h1 className='public-tender__detail-title'>
            {data.titleProcurement}
          </h1>
          <div className='d-flex flex-wrap align-items-center gap-2 mt-2'>
            <span className='text-muted'>
              {formatText(data.procurementNumber)}
            </span>
            <TenderStatusBadge status={data.publicStatus} />
            <span className='text-muted small'>
              {t('detail.viewCount', {
                count: formatCount(data.viewCount, language),
              })}
            </span>
          </div>
        </div>
      )}

      <div className='public-tender__body'>
        <Card className='public-tender__detail-card'>
          <CardBody>
            <h5 className='public-tender__card-title'>
              {t('detail.cardInfo')}
            </h5>
            <div className='table-responsive'>
              <Table className='public-tender__detail-table mb-0'>
                <tbody>
                  {infoRows.map(([label, value]) => (
                    <tr key={label}>
                      <th scope='row'>{label}</th>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        <Card className='public-tender__detail-card'>
          <CardBody>
            <h5 className='public-tender__card-title'>
              {t('detail.cardSummary')}
            </h5>
            <p className='mb-0'>{formatText(data.summary)}</p>
          </CardBody>
        </Card>

        <Card className='public-tender__detail-card'>
          <CardBody>
            <h5 className='public-tender__card-title'>
              {t('detail.cardAttachments')}
            </h5>
            {data.attachments.length === 0 ? (
              <p className='text-muted mb-0'>{t('detail.noAttachment')}</p>
            ) : (
              <ul className='public-tender__attachment-list'>
                {data.attachments.map(file => (
                  <li key={file.id} className='public-tender__attachment-item'>
                    <i
                      className='ri-file-pdf-line public-tender__attachment-icon'
                      aria-hidden='true'
                    ></i>
                    <div className='flex-grow-1'>
                      <div className='public-tender__attachment-name'>
                        {file.fileName}
                      </div>
                      <div className='text-muted small'>
                        {formatFileSize(file.fileSizeBytes, language)}
                      </div>
                    </div>
                    <Button
                      tag='a'
                      href={`${API_ORIGIN}${file.downloadUrl}`}
                      target='_blank'
                      rel='noreferrer'
                      color='primary'
                      outline
                      size='sm'
                    >
                      <i
                        className='ri-download-2-line me-2'
                        aria-hidden='true'
                      ></i>
                      {t('detail.download')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Tautan aksi (D-37). Kartu tidak dirender sama sekali bila kosong —
            bukan kotak kosong berisi keterangan. Isi Description dirender
            sebagai TEKS BIASA; dilarang dangerouslySetInnerHTML karena teks ini
            ditulis admin dan dibaca pengunjung anonim. */}
        {actionLinks.length > 0 && (
          <div className='public-tender__action-links'>
            {actionLinks.map((link, index) => (
              <div
                key={`${link.targetUrl}-${index}`}
                className='public-tender__action-link'
              >
                <p className='public-tender__action-link-desc'>
                  {link.description}
                </p>
                <Button
                  tag='a'
                  href={link.targetUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  color='primary'
                >
                  {link.buttonLabel}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PublicTenderAnnouncementDetail;
